import { Dayjs } from 'dayjs';
import { FlagGroups } from '../constants/flags';
import { ClinicLocation } from './Clinic.type';
import { Macro } from './Macro';
import { SupportAccessContextPublic } from './SupportAccess.type';
import { UserRoles } from './User.type';

export type Prices = {
  basic: number;
  plus: number;
  premium: number;
  ehr: number;
  additionalLocations: number;
  /** ISO currency the numbers above are denominated in ('USD' | 'CAD' | 'ZAR'). */
  currency?: string;
  coupon?: {
    amount_off?: number;
    percent_off?: number;
    duration?: string;
    duration_in_months?: number;
    code?: string;
    appliesToEverything?: boolean;
    freeMonth?: boolean;
  };
};

export type SubscriptionProduct = {
  id: string;
  name?: string;
};

export type SubscriptionPriceRecurring = {
  interval?: 'day' | 'week' | 'month' | 'year';
  interval_count?: number;
  usage_type?: 'licensed' | 'metered';
};

export type SubscriptionPrice = {
  id: string;
  currency?: string;
  nickname?: string | null;
  product?: string | SubscriptionProduct;
  recurring?: SubscriptionPriceRecurring | null;
  unit_amount?: number | null;
};

export type SubscriptionItem = {
  id: string;
  price: SubscriptionPrice;
  quantity?: number | null;
};

export type Clinician = {
  ID: string;
  name: string;
  degree: string;
  fname: string;
  lname: string;
  profileImage: string | null;
  supervisor?: string | null;
  billingSupervision: boolean;
  encounterSupervision: boolean;
  supervisedTreatments: number[];
  locationIds: number[];
};

export type Subscription = {
  amount?: number;
  id?: string;
  items?: SubscriptionItem[];
  name?: string;
  plan?: string;
  pastDueDays?: number;
  subscriptionPrices?: Prices;
  pauseCollectionUntil?: number;
  profile?: {
    customerProfileId?: string;
    description?: string;
    merchantCustomerId?: string;
    paymentProfile?: {
      billTo?: {
        firstName: string;
        lastName: string;
      };
      customerPaymentProfileId?: string;
      customerType?: string;
      payment: {
        creditCard?: { cardNumber: string; expirationDate: string };
        bankAccount?: {
          accountNumber: string;
          routingNumber: string;
        };
      };
    };
  };
  status?:
    | 'active'
    | 'expired'
    | 'suspended'
    | 'canceled'
    | 'terminated'
    | 'past_due'
    | 'requires_action'
    | 'incomplete'
    | 'trialing';
  trialAmount?: number;
  locations?: number;
  created?: string;
  callForHelp?: boolean;
  cancel_at_period_end?: boolean;
  current_period_end?: number;
  hasVoiceToChartAccess?: boolean;
  hasAdvancedMessagingAccess?: boolean;
  hasAiReceptionistAccess?: boolean;
};

export type PriceTiers = {
  basic: number;
  plus: number;
  premium: number;
  ehr: number;
  trial?: boolean;
  education?: boolean;
};

export type Plan = keyof PriceTiers;

export type MeClinicSettingType = {
  ID: number;
  section: string;
  setting: string;
  jsonValue: any;
};

export type MeClinic = {
  ID: number;
  name: string;
  location: string;
  logo: string;
  language: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  fax: string;
  email: string;
  ehr?: boolean;
  site: string;
  facebook: string;
  x: string;
  youtube: string;
  surveyOptOut: boolean;
  theme: string;
  plan: Plan;
  clinicians: Clinician[];
  role: UserRoles[];
  subscription: Subscription;
  bypassPaymentGateway: boolean;
  freeAccount: boolean;
  subscriptionAmount: number;
  subscriptionID: number;
  trialCount: number;
  trialEndDate?: string;
  subscriptionStartDate?: string;
  googleReviewPlaceID?: string;
  prices?: Prices;
  settings?: MeClinicSettingType[];
  billDate: string;
  termsOfUse: string;
  expired?: boolean;
  kioskID: string;
  primaryColor?: string;
  accentColor?: string;
  noPatientCommunication?: number;
  updatingIcons: number;
  stripePaymentMethod?: any;
  stripeCustomerId?: string;
  stripeCustomerIdHash?: string;
  stripeSubscriptionId?: string;
  stripeSubscriptionStatus?: string;
  stripeFailureMessage?: string;
  stripePendingSetupIntentId?: string | null;
  /**
   * Stripe-hosted microdeposit verification URL containing a short-lived session token.
   * Treat as a credential — do NOT log, add to Sentry payloads, or include in analytics events.
   */
  stripePendingVerificationUrl?: string | null;
  referralCode?: string;
  gradCouponName?: string;
  flags?: {
    [key: string]: boolean;
  };
  flagGroup?: FlagGroups;
  locations: ClinicLocation[];
  postingSchedule?: 'A' | 'B' | null;
  plusUntil?: Date | null;
  premiumUntil?: Date | null;
  ehrMigrationAllowed?: boolean;
  patientMergeAllowed?: boolean;
  signedNoteTimestamp?: boolean;
  hasNCMICPay?: boolean;
  merchantAccounts?: { merchantId: number; name: string }[];
  /** Minutes of Voice to Chart usage for the current billing cycle */
  voiceToChartMinutes?: number;
  /** Whether clinic has access to Voice to Chart (requires EHR) */
  hasVoiceToChartAccess?: boolean;
  /** Whether clinic has paid Voice to Chart subscription (1500 minutes vs 100 free) */
  hasVoiceToChartSubscription?: boolean;
  /** Whether clinic has an active AI Receptionist subscription */
  hasAiReceptionistAccess?: boolean;
  /** Unix timestamp of when the Voice to Chart subscription ends */
  voiceToChartEndDate?: number;
  /** Date by which EHR payment must be resolved before access is auto-revoked */
  ehrGracePeriodEndDate?: string | null;
  /** Whether clinic has access to Advanced Messaging features */
  hasAdvancedMessagingAccess?: boolean;
  /** Whether clinic has paid Advanced Messaging subscription */
  hasAdvancedMessagingSubscription?: boolean;
};

export type StudentRecord = {
  status: string;
  graduationDate: string;
  studentId?: string;
};

/**
 * A clinic membership an admin created for the user that is still awaiting the
 * user's accept/reject decision (UserClinic.status = 'PENDING'). These are NOT
 * included in `Me.clinics` — the user cannot use the clinic until they accept.
 */
export type PendingClinicInvite = {
  clinicId: number;
  clinicName: string;
  logo?: string | null;
  role: UserRoles[];
};

export type Me = {
  ID: string;
  clinics: MeClinic[];
  /** Clinic invitations awaiting the user's accept/reject decision. */
  pendingInvites?: PendingClinicInvite[];
  createDate?: string;
  dashboardData?: {
    knowledgebase: any[];
    videos: any[];
    walkthroughs: any[];
  };
  degree: string;
  email: string;
  fname: string;
  middleInitial?: string;
  suffix?: string;
  isProvider?: boolean;
  lname: string;
  mfa: boolean;
  name: string;
  phone: string;
  reactivationDate?: string;
  selectedClinic?: MeClinic;
  selectedLocation?: number;
  signature?: string;
  profileImage: string | null;
  bio?: string;
  studentRecord?: StudentRecord;
  surveyOptOut: boolean;
  termsCurrent?: boolean;
  title: string;
  macros?: Omit<Macro, 'userID' | 'createdAt'>[];
  hasAcceptedEHRTerms: Dayjs | null;
  supervisor?: string;
  encounterSupervision?: boolean;
  billingSupervision?: boolean;
  defaultEpayPublicKey?: string;
  showImpersonator?: boolean;
  mfaResetPending?: boolean;
  /** User's preferred MFA factor for sign-in. UI uses this to pre-select the picker; server never sets PREFERRED in Cognito (that would suppress the picker). */
  preferredMfa?: 'SMS' | 'TOTP' | 'EMAIL' | null;
  /** True when the user has configured a 6-digit PIN for inactivity-lock unlock. The hash itself is never sent to the client. */
  hasPinConfigured?: boolean;
  /** True when the server has the inactivity lock set for this user. Server is the source of truth; the frontend overlay reflects this flag. */
  locked?: boolean;
  /** Cognito ID-token `auth_time` (Unix seconds) for this sign-in session. Stable across silent token refreshes; resets only on a fresh interactive sign-in. */
  authTime?: number;
  supportAccess?: SupportAccessContextPublic;
};

/**
 * Protected by closure. Doing this once may not provide any performance
 * benefit, but it is a decent pattern to follow.
 */
let settingValuesByKey: { [key: number]: { [key: string]: any } };

/**
 * If the jsonObject on the setting is not an array, the 'key' in this will
 * be the property names on that object. If it is an array, the key will be
 * the setting name.
 *
 * This provides a common way to retrieve the value of a property inside an
 * extended setting.
 *
 * @param clinic
 * @param name
 * @returns
 */
export const getExtendedSettingValueByKey = (clinic: MeClinic | null, name: string): any => {
  if (!clinic) return null;

  settingValuesByKey = settingValuesByKey || {};

  if (!settingValuesByKey[clinic.ID]) {
    settingValuesByKey[clinic.ID] = (clinic.settings || []).reduce(
      (acc: { [key: string]: any }, obj: MeClinicSettingType) => {
        if (Array.isArray(obj.jsonValue)) {
          acc[obj.setting] = obj.jsonValue;
        } else {
          Object.keys(obj.jsonValue).forEach(key => {
            acc[key] = obj.jsonValue[key];
          });
        }

        return { ...acc };
      },
      {}
    );
  }

  return settingValuesByKey[clinic.ID][name];
};
